// Crypto Wallets → Configuration → Assets (SM-267, narrowed by SM-507).
//
// Asset DEFINITIONS and nothing else: which network a token lives on,
// what it is called, how it is denominated on chain, and the smallest
// deposit worth crediting. These are SHARED references — one row per
// token, never one per platform currency it happens to be priced into.
//
// Two things used to share this page and no longer do, because both are
// per-(asset, currency) facts that a per-asset page can only show for
// one currency at a time:
//
//   top-up rates                 → Configuration → Asset Rates
//   deposit/withdraw switches    → Configuration → Deposit & Withdrawal
//
// Editing a definition here therefore changes what a token IS, never
// what it is worth or whether it may move.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { TechHint } from "@/components/ui/TechHint";
import { TokenAmountInput } from "@/components/ui/TokenAmountInput";
import { formatTokenAmount, parseTokenToBase } from "@/lib/tokenFmt";
import {
  errMsg,
  td,
  th,
  thRight,
  useCanConfig,
} from "@/components/wallet-ops/configShared";
import {
  fetchCryptoAssets,
  fetchCryptoNetworks,
  upsertCryptoAsset,
  walletKeys,
} from "@/lib/wallet-queries";
import type { CryptoAsset, CryptoNetwork } from "@/lib/wallet-types";

export const Route = createFileRoute("/_app/wallet-ops/config/assets")({
  component: AssetsConfigPage,
});

function AssetsConfigPage() {
  const canConfig = useCanConfig();
  const networks = useQuery({
    queryKey: walletKeys.networksList(false),
    queryFn: () => fetchCryptoNetworks(),
  });
  const assets = useQuery({
    queryKey: walletKeys.assets(),
    queryFn: () => fetchCryptoAssets(),
  });

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <h1 className="font-display text-2xl tracking-tight">Assets</h1>
        <p className="max-w-[85ch] text-sm text-muted">
          Every deposit token's definition: the network it lives on, how it
          is denominated on chain, and the smallest amount worth crediting.
          One row per token — an asset is referenced by every currency it is
          priced into, never copied for each of them.
        </p>
      </header>
      <MovedElsewhere />
      <AssetsSection
        assets={assets.data ?? []}
        networks={networks.data ?? []}
        loading={assets.isLoading}
        canConfig={canConfig}
      />
    </div>
  );
}

// What used to be on this page, and where it went. Kept as a permanent
// signpost rather than a release note: an operator who knows this screen
// as "Assets & Rates" will keep arriving here looking for a rate, and a
// page that simply no longer has one reads as a page that lost it.
function MovedElsewhere() {
  return (
    <div className="rounded-md border border-dashed border-border p-4">
      <p className="text-xs text-t-3">
        Rates and availability belong to an asset and a currency together, so
        they live on their own pages — one asset can be priced into two
        currencies, and opened for one of them only.
      </p>
      <div className="mt-2 flex flex-wrap gap-4 text-xs">
        <Link
          to="/wallet-ops/config/asset-rates"
          className="underline underline-offset-2 hover:text-t-1"
        >
          Top-up rates → Asset Rates
        </Link>
        <Link
          to="/wallet-ops/config/rails"
          className="underline underline-offset-2 hover:text-t-1"
        >
          Deposit and withdraw switches → Deposit &amp; Withdrawal
        </Link>
      </div>
    </div>
  );
}

// --- assets ---

// A brand-new asset ships fully dark: both capabilities are opened later
// on Deposit & Withdrawal, never at creation time. asset_id is left blank
// so the server mints a ULID — operators never author it; on edit the
// existing id rides along in `form` to target the row.
const EMPTY_ASSET: CryptoAsset = {
  asset_id: "",
  network_id: "",
  symbol: "",
  kind: "NATIVE",
  token_contract: "",
  token_decimals: 18,
  min_deposit_base: "",
  deposit_enabled: false,
  withdrawal_enabled: false,
};

function AssetsSection({
  assets,
  networks,
  loading,
  canConfig,
}: {
  assets: CryptoAsset[];
  networks: CryptoNetwork[];
  loading: boolean;
  canConfig: boolean;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<CryptoAsset | null>(null);
  const save = useMutation({
    mutationFn: (input: CryptoAsset) => upsertCryptoAsset(input),
    onSuccess: async () => {
      setForm(null);
      await qc.invalidateQueries({ queryKey: walletKeys.all });
    },
    meta: { successMessage: "Asset saved." },
  });

  const set = <K extends keyof CryptoAsset>(k: K, v: CryptoAsset[K]) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));

  const networkOptions = networks.map((n) => ({
    value: n.network_id,
    label: n.name || n.network_id,
  }));

  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle>Assets</CardTitle>
        {canConfig && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setForm(form ? null : { ...EMPTY_ASSET })}
          >
            {form ? "Cancel" : "New asset"}
          </Button>
        )}
      </CardHeader>
      <CardBody className="p-0">
        {form && (
          <form
            className="space-y-4 border-b border-border p-5"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate(form);
            }}
          >
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Select
                label="Network"
                value={form.network_id}
                onChange={(e) => set("network_id", e.target.value)}
                placeholder="Pick a network"
                options={networkOptions}
              />
              <Input
                label="Symbol"
                value={form.symbol}
                onChange={(e) => set("symbol", e.target.value)}
                placeholder="USDC"
                required
              />
              <Select
                label="Kind"
                value={form.kind}
                onChange={(e) => set("kind", e.target.value)}
                options={[
                  { value: "NATIVE", label: "Native" },
                  { value: "ERC20", label: "ERC-20" },
                ]}
              />
              <Input
                label="Token contract"
                value={form.token_contract}
                onChange={(e) => set("token_contract", e.target.value)}
                placeholder="0x… (ERC-20 only)"
                className="font-mono"
              />
              <Input
                label="Token decimals"
                value={String(form.token_decimals)}
                onChange={(e) =>
                  set("token_decimals", Number(e.target.value) || 0)
                }
                inputMode="numeric"
              />
              <TokenAmountInput
                label="Min deposit"
                symbol={form.symbol || undefined}
                base={form.min_deposit_base}
                decimals={form.token_decimals}
                onBase={(b) => set("min_deposit_base", b)}
                allowEmpty
                emptyHint="empty = no minimum"
              />
            </div>
            <p className="text-2xs text-muted">
              New assets ship dark. Price one on Asset Rates and open it on
              Deposit &amp; Withdrawal once it is ready.
            </p>
            {save.isError && (
              <p className="text-2xs text-no">{errMsg(save.error)}</p>
            )}
            <Button type="submit" loading={save.isPending}>
              Save asset
            </Button>
          </form>
        )}
        {loading && (
          <div className="space-y-2 p-5">
            {[0, 1].map((i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        )}
        {!loading && assets.length === 0 && (
          <p className="px-5 py-10 text-center text-sm text-muted">
            No assets configured yet.
          </p>
        )}
        {assets.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-surface-2">
                <tr>
                  <th className={`${th} px-5`}>Asset</th>
                  <th className={th}>Network</th>
                  <th className={th}>Kind</th>
                  <th className={th}>Contract</th>
                  <th className={thRight}>Decimals</th>
                  <th className={thRight}>Min deposit</th>
                  {canConfig && <th className={thRight} />}
                </tr>
              </thead>
              <tbody>
                {assets.map((a) => (
                  <tr
                    key={a.asset_id}
                    className="border-b border-border/60 last:border-0"
                  >
                    <td className={`${td} px-5 font-medium text-fg`}>
                      {a.symbol}
                      <span className="ml-2 font-mono text-2xs text-muted">
                        {a.asset_id}
                      </span>
                    </td>
                    <td className={td} title={a.network_id}>
                      {networks.find((n) => n.network_id === a.network_id)?.name || a.network_id}
                    </td>
                    <td className={td}>
                      <Badge tone={a.kind === "NATIVE" ? "accent" : "muted"}>
                        {a.kind}
                      </Badge>
                    </td>
                    <td className={`${td} max-w-[180px] truncate font-mono text-2xs`}>
                      {a.token_contract || "—"}
                    </td>
                    <td className={`${td} num text-right`}>{a.token_decimals}</td>
                    <td className={`${td} num text-right`}>
                      {a.min_deposit_base ? (
                        <TechHint tech={`${a.min_deposit_base} base units`}>
                          {formatTokenAmount(a.min_deposit_base, a.token_decimals, a.symbol)}
                        </TechHint>
                      ) : (
                        "—"
                      )}
                    </td>
                    {canConfig && (
                      <td className={`${td} text-right`}>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setForm({ ...a })}
                        >
                          Edit
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
